import { useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { PauseCircle, ShieldCheck } from 'lucide-react';

export const DutiSettingsPanel = ({ settings, onChange }) => {
  const [saving, setSaving] = useState(null);
  const canManage = settings.can_manage;

  const save = async (key, value) => {
    setSaving(key);
    try {
      const { data } = await api.put('/duti/settings', { [key]: value });
      onChange({ ...settings, ...data });
      toast.success('Duti AI settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(null);
    }
  };

  return (
    <section className="space-y-6 max-w-3xl" data-testid="duti-settings-panel">
      <div className={`flex items-center justify-between gap-4 rounded-xl border p-5 ${settings.paused ? 'bg-amber-50 border-amber-300' : 'bg-card'}`}>
        <div className="flex gap-3">
          <PauseCircle className="h-5 w-5 mt-0.5 text-amber-600" />
          <div>
            <p className="font-medium">Pause hands-on abilities</p>
            <p className="text-sm text-muted-foreground">Questions and lookups keep working while paused.</p>
          </div>
        </div>
        <Switch checked={settings.paused} disabled={!canManage || saving === 'paused'} onCheckedChange={(v) => save('paused', v)} data-testid="duti-pause-switch" />
      </div>

      <div className="rounded-xl border divide-y">
        {settings.abilities_meta.map((a) => (
          <div key={a.key} className="flex items-center justify-between gap-4 p-5" data-testid={`duti-ability-row-${a.key}`}>
            <div>
              <p className="font-medium">{a.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Staff also need their own “{a.permission.replace(/_/g, ' ')}” permission.
                {!a.you_can && <Badge variant="outline" className="ml-2 text-[10px]">You don't have it</Badge>}
              </p>
            </div>
            <Switch
              checked={!!settings.abilities[a.key]}
              disabled={!canManage || saving === a.key}
              onCheckedChange={(v) => save(a.key, v)}
              data-testid={`duti-ability-switch-${a.key}`}
            />
          </div>
        ))}
      </div>
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="h-4 w-4" />
        Duti AI only ever drafts — every change waits for approval by someone who already holds that permission.
        {!canManage && ' Only the owner can change these settings.'}
      </p>

      <div className="rounded-xl border divide-y" data-testid="duti-alerts-panel">
        <div className="p-5">
          <p className="font-medium">Approval alerts</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Tell the people who can approve a draft as soon as Duti AI makes one. The morning restock orders arrive as one summary.
            Uses your pharmacy's email and SMS setup; staff need an email or phone number on their account.
          </p>
        </div>
        {[['alert_email', 'email', 'Email alerts'], ['alert_sms', 'sms', 'SMS alerts']].map(([key, k, label]) => (
          <div key={key} className="flex items-center justify-between gap-4 p-5">
            <p className="text-sm">{label}</p>
            <Switch checked={!!settings.alerts?.[k]} disabled={!canManage || saving === key}
              onCheckedChange={(v) => save(key, v)} data-testid={`duti-alert-switch-${k}`} />
          </div>
        ))}
      </div>
    </section>
  );
};
